import { useModal } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { MemoryRouter } from "react-router-dom";
import { AssignRouteHeader } from "~/features/assign-route/ui/assign-route-header";
import { createMapVisibility } from "~/features/assign-route/model/map-visibility";
import { PseudoRouteContent } from "../ui/pseudo-route-content";

export interface OpenPseudoRouteOptions {
  /** Called after a pseudo-route is created, e.g. to refresh the route list. */
  onCreated?: () => void;
}

/**
 * Opens the "add pseudo-route" modal — the same shape as the assign-route one
 * (sidebar, shift header, job list, map), except every stop is a job that does
 * not exist yet and the route is recorded after the fact.
 */
export function usePseudoRouteModal() {
  const { openModal } = useModal();
  const t = useT();

  const openPseudoRoute = (options: OpenPseudoRouteOptions = {}) => {
    // Shared by the header's toggle and the body's map panel — see map-visibility.
    const mapVisibility = createMapVisibility();

    return openModal({
      size: "custom",
      width: { lg: "80svw" },
      height: { lg: "80svh" },
      // The header is the assign-route one: title plus the map toggle, and it
      // is driven entirely by the title and store handed to it.
      title: (
        <AssignRouteHeader
          title={t(Namespace.Routes, "addPseudoRouteTitle")}
          mapVisibility={mapVisibility}
        />
      ),
      closeButtonTop: "top-5",
      // `[&>div]:w-full` stretches the title wrapper ModalProvider adds so the
      // header row can push the map toggle to the right; `pr-12` keeps it clear
      // of the absolutely positioned close button.
      headerClassName: "!p-4 !pr-12 [&>div]:w-full",
      // `h-full` would make the body as tall as the whole modal and push the
      // footer past its bottom edge — it has to take the space left by the header.
      bodyClassName: "!p-0 min-h-0 flex-1",
      // ModalProvider lives above the app router, so the modal body has no
      // router context — the map's useInfoPanel needs one.
      body: (
        <MemoryRouter>
          <PseudoRouteContent mapVisibility={mapVisibility} onCreated={options.onCreated} />
        </MemoryRouter>
      ),
    });
  };

  return { openPseudoRoute };
}
